import { productTitle } from "@/lib/productNaming";

// §29.3: shared deterministic normalized product search — no LLM.
// Handles MZ001 / MZ 001 / MZ-001 / 001, color names with accent folding and
// 1-edit typos, and format expressions 4×4/4x4/4 x 4/4 by 4/four by four
// (plus 2×6/two by six/Bejmat and 2×2 equivalents).

const WORD_FOLDS = { four: "4", two: "2", six: "6", by: "x" };

export function foldSearch(str) {
  let s = (str || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  s = s.replace(/×/g, "x");
  s = s.replace(/\b(four|two|six|by)\b/g, (m) => WORD_FOLDS[m]);
  s = s.replace(/(\d)\s*x\s*(\d)/g, "$1x$2");
  s = s.replace(/mz[\s-]?(\d+)/g, "mz$1");
  return s.replace(/\s+/g, " ").trim();
}

function levWithin(a, b, max = 1) {
  if (Math.abs(a.length - b.length) > max) return false;
  const dp = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
        // Damerau transposition counts as one edit
        i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]
          ? dp[i - 2][j - 2] + 1
          : Infinity,
      );
    }
  }
  return dp[a.length][b.length] <= max;
}

export const FORMAT_LABEL = { "4x4": "4×4", "2x6": "2×6 Bejmat", "2x2": "2×2" };

function haystack(v) {
  const parts = [
    v.sku, v.label, v.color_code, v.color_name, v.us_color_name, v.product_name, v.format_in,
    FORMAT_LABEL[v.format_in], v.presentation,
    (v.search_tokens || []).join(" "),  // CAT-160: approved governance mappings only
    v.format_in === "2x6" ? "bejmat" : "",
  ];
  return foldSearch(parts.filter(Boolean).join(" "));
}

export function variantMatches(v, query) {
  const q = foldSearch(query);
  if (!q) return true;
  // V4.6 (CAT-151): an explicit SKU token is format-bound — "mz001" matches ONLY the
  // MZ (4×4) SKU, never the sibling BJ/MD variants of the same color; a bare prefix
  // ("mz"/"bj"/"md") filters to that format family.
  // ID-012: progressive SKU typing — "m", "mz", "mz0", "mz00", "mz004" narrow by prefix; a short
  // digit token also matches by value ("mz1" → MZ001). A full 3-digit SKU stays format-bound (CAT-151).
  const skuTok = q.match(/^(mz|bj|md)(\d{1,3})$/);
  if (skuTok) {
    const sku = foldSearch(v.sku || "");
    return sku.startsWith(q) || (skuTok[2].length < 3 && sku.startsWith(skuTok[1]) && parseInt(sku.slice(2), 10) === parseInt(skuTok[2], 10));
  }
  if (/^(m|b|mz|bj|md)$/.test(q)) return foldSearch(v.sku || "").startsWith(q);
  const hay = haystack(v);
  if (hay.includes(q)) return true;
  return q.split(" ").every((qt) =>
    hay.split(" ").some((ht) =>
      ht.includes(qt) || qt.includes(ht) || (qt.length >= 4 && levWithin(qt, ht, 1))));
}

// Clean, non-duplicative identity: "MZ001 · HRECH · 4×4"
export function variantIdentity(v) {
  // RC49-NC-001: single global canonical title (code prefix kept for search recognition)
  return [v.color_code, v.product_title || productTitle(v)].filter(Boolean).join(" · ");
}
